import React, { useMemo } from 'react';
import Page from '../../components/Page/Page';
import { Collapse, Typography } from 'antd';
import Icon from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from './FAQPage.module.less';
import { FAQList, getFAQ } from '../../utils/faq';
import QuestionDetails from './QuestionDetails';
import Button from '../../components/Button/Button';
import Title from '../../components/Page/Title';
import TitleIcon from '../../assets/img/titleIcon.svg?react';
import { useHistory, useLocation } from 'react-router-dom';
import { routes } from '../../utils/routerHelper';

type SelectedQuestion = { category: number; question: number };

type FAQContentProps = {
  faqList: FAQList;
  onQuestionSelect: (question: SelectedQuestion) => void;
};

function FAQContent(props: FAQContentProps) {
  const { t } = useTranslation();
  const { onQuestionSelect, faqList } = props;
  return (
    <div>
      <div className={styles.introContainer}>
        <div>
          <Typography.Title level={3}>{t('faq.intro.title')}</Typography.Title>
        </div>
        <div>
          <Typography.Text>{t('faq.intro.text')}</Typography.Text>
        </div>
      </div>
      <Collapse
        className={styles.accordion}
        bordered={false}
        accordion
        expandIconPosition={'right'}
        expandIcon={panelProps => {
          if (panelProps.isActive) {
            return <Icon type="minus-circle" className={styles.expandIcon} />;
          }
          return <Icon type="plus-circle" className={styles.expandIcon} />;
        }}
      >
        {faqList.map((faq, faqIndex) => (
          <Collapse.Panel header={faq.category} key={faqIndex} data-testid={'test'}>
            {faq.questions.map((question, questionIndex) => (
              <div
                key={questionIndex}
                className={styles.questionContainer}
                onClick={() =>
                  onQuestionSelect({
                    category: faqIndex,
                    question: questionIndex
                  })
                }
              >
                <p>{question.question}</p>
                <Icon type="right" className={styles.showAnswerIcon} />
              </div>
            ))}
          </Collapse.Panel>
        ))}
      </Collapse>
    </div>
  );
}

function FAQPage() {
  const { t } = useTranslation();
  const faqList = useMemo(() => getFAQ(), [t]);
  const { search } = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const categoryIndex = urlParams.get('category');
  const questionIndex = urlParams.get('question');

  // eslint-disable-next-line no-console
  console.log(`categoryIndex ${categoryIndex} questionIndex ${questionIndex}`);
  const history = useHistory();

  const selectedQuestion =
    !!categoryIndex && !!questionIndex
      ? {
          category: +categoryIndex,
          question: +questionIndex
        }
      : undefined;

  const setSelectedQuestion = useMemo(
    () => (question: SelectedQuestion | undefined) => {
      const searchParams = question
        ? '?' + new URLSearchParams({ category: `${question.category}`, question: `${question.question}` }).toString()
        : routes.faq;
      history.push(searchParams);
    },
    []
  );

  return (
    <Page>
      <div className={styles.pageTitleContainer}>
        <Title text={t('faq.pageTitle')} Icon={TitleIcon} />
        {selectedQuestion && (
          <div className={styles.backButtonContainer}>
            <Button block onClick={() => setSelectedQuestion(undefined)} data-testid={'backButton'}>
              {t('faq.backToFAQ')}
            </Button>
          </div>
        )}
      </div>
      {selectedQuestion ? (
        <QuestionDetails
          question={faqList[selectedQuestion.category].questions[selectedQuestion.question]}
          onBackClick={() => {
            setSelectedQuestion(undefined);
          }}
        />
      ) : (
        <FAQContent onQuestionSelect={setSelectedQuestion} faqList={faqList} />
      )}
    </Page>
  );
}

export default FAQPage;
